import type { AssetWithDevices } from '../../AssetsHelpGang.tsx';
import { ValueWithCopyButton } from '../../../components/ValueWithCopyButton.tsx';
import type { Asset, Device } from '../../../api/assetAdministrationBackendCodec.ts';
import { DisasociateDevice } from './action/DisassociateDevice.tsx';
import ExpanderPanel from '@rio-cloud/rio-uikit/components/expander/ExpanderPanel';
import type { ReactNode } from 'react';

interface AssetDetailsProps {
    asset: AssetWithDevices | undefined;
}

export const AssetDetails = ({ asset }: AssetDetailsProps) => {
    if (asset === undefined) {
        return;
    }

    const masterData = asset.asset._embedded?.master_data;

    return (
        <div className='width-100pct'>
            <table className={'table table-head-filled table-condensed'}>
                <thead>
                    <tr>
                        <th>Properties</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Asset Id</td>
                        <td>{asset.asset.id}</td>
                    </tr>
                    <tr>
                        <td>VIN</td>
                        <td>{asset.asset.identification}</td>
                    </tr>
                    <tr>
                        <td>Account Id</td>
                        <td>{asset.asset.account_id}</td>
                    </tr>
                    <tr>
                        <td>Name</td>
                        <td>{asset.asset.name}</td>
                    </tr>
                    <tr>
                        <td>Brand</td>
                        <td>{asset.asset.brand}</td>
                    </tr>
                    <tr>
                        <td>License plate</td>
                        <td>{asset.asset.license_plate}</td>
                    </tr>
                </tbody>
            </table>
            {masterData && (
                <ExpanderPanel
                    open={true}
                    title={
                        <table className='table table-condensed'>
                            <thead>
                                <tr className={'text-color-dark bg-lightest'}>
                                    <td colSpan={2} style={{ paddingLeft: '12px' }}>
                                        Master Data
                                    </td>
                                </tr>
                            </thead>
                        </table>
                    }
                    headerClassName='padding-0 position-relative text-color-dark bg-lightest'
                    titleClassName='width-100pct margin-0 overflow-hidden'
                    bodyClassName='padding-0'
                    iconClassName='margin-right-5'
                    className='overflow-hidden rounded-none'
                >
                    <table className='table table-condensed table-layout-fixed'>
                        <tbody>{toTableRows(masterData)}</tbody>
                    </table>
                </ExpanderPanel>
            )}
            <ExpanderPanel
                open={true}
                title={
                    <table className='table table-condensed'>
                        <thead>
                            <tr className={'text-color-dark bg-lightest'}>
                                <td colSpan={2} style={{ paddingLeft: '12px' }}>
                                    Data Sources
                                </td>
                            </tr>
                        </thead>
                    </table>
                }
                headerClassName='padding-0 position-relative text-color-dark bg-lightest'
                titleClassName='width-100pct margin-0 overflow-hidden'
                bodyClassName='padding-0'
                iconClassName='margin-right-5'
                className='overflow-hidden rounded-none'
            >
                <table className='table table-condensed table-layout-fixed'>
                    <tbody>
                        {asset.devices.map(device => (
                            <DataSource key={device.id} asset={asset.asset} device={device} />
                        ))}
                    </tbody>
                </table>
            </ExpanderPanel>
        </div>
    );
};

type TableObject = Record<string, unknown>;

const isTableObject = (value: unknown): value is TableObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isTableObjectArray = (value: unknown): value is TableObject[] =>
    Array.isArray(value) && value.every(isTableObject);

const toTableRows = (object: TableObject, prefix = ''): ReactNode[] =>
    Object.entries(object).map(([key, value]): ReactNode => {
        if (!value) {
            return null;
        }

        if (isTableObjectArray(value)) {
            return (
                <tr key={key}>
                    <td>{prefix + humanizeKey(key)}</td>
                    <td style={{ padding: '0px' }}>
                        <table>
                            <tbody>{value.map(item => toTableRows(item))}</tbody>
                        </table>
                    </td>
                </tr>
            );
        }

        if (isTableObject(value)) {
            return toTableRows(value, `${prefix + humanizeKey(key)} > `);
        }

        return (
            <tr key={key}>
                <td>{prefix + humanizeKey(key)}</td>
                <td>{String(value)}</td>
            </tr>
        );
    });

const humanizeKey = (key: string) =>
    key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

export const DataSource = ({ asset, device }: { asset: Asset; device: Device }) => (
    <>
        <tr>
            <td>Type</td>
            <td>{device.type}</td>
        </tr>
        <tr>
            <td>Device Identification</td>
            <td>{device.identification}</td>
        </tr>
        <tr>
            <td>Device Id</td>
            <td>
                <ValueWithCopyButton value={device.id} tooltipText={'Copy Id'}>
                    {device.id}
                </ValueWithCopyButton>
            </td>
        </tr>
        <tr>
            <td />
            <td>
                <div className={'pull-right margin-bottom-10'}>
                    <DisasociateDevice asset={asset} device={device} />
                </div>
            </td>
        </tr>
    </>
);
